import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Download } from '@mui/icons-material';
import { colors } from '../theme/modernTheme';

/**
 * Preview Modal Component
 * Full-screen modal for previewing receipts with PDF download capability
 * PDF download is handled by the backend (server-side rendering via PDFKit)
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.content - HTML content to render (receipt)
 * @param {string} [props.emailTemplateHtml] - Optional email template HTML to display alongside receipt
 * @param {string} [props.title] - Optional modal title
 * @param {Function} [props.onDownloadPDF] - Callback for PDF download (calls backend)
 * @param {string} [props.downloadFilename] - Optional filename for PDF download
 * @param {string} [props.previewType] - Type of preview: 'receipt' or 'email'
 */
export default function PreviewModal({
  open,
  onClose,
  content,
  emailTemplateHtml,
  title = 'Preview',
  onDownloadPDF,
  downloadFilename = 'receipt-preview.pdf',
  previewType = 'receipt',
}) {
  const contentRef = useRef(null);
  const [downloading, setDownloading] = React.useState(false);

  const handleDownloadPDF = async () => {
    if (!onDownloadPDF) return;

    try {
      setDownloading(true);
      await onDownloadPDF();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: colors.background.default,
        },
      }}
    >
      {/* Close Button - Top Left */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1300,
          color: colors.text.primary,
          fontWeight: 900,
          '&:hover': {
            backgroundColor: 'transparent !important',
            color: colors.text.dark,
          },
          fontSize: '24px !important',
        }}
      >
        <CloseIcon sx={{ fontSize: '16px !important' }} />
      </IconButton>

      {/* Download Button - Top Right */}
      <Tooltip title="Download PDF: Header image and footer image will not be included">
        <Button
          onClick={handleDownloadPDF}
          startIcon={
            downloading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Download sx={{ fontSize: '16px !important' }} />
            )
          }
          disabled={downloading}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1300,
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            fontSize: '14px !important',
            border: `1px solid ${downloading ? colors.text.disabled : colors.primary.main}`,
            color: colors.primary.main,
            '&:hover': {
              color: colors.primary.dark,
            },
          }}
        >
          {downloading ? 'Generating PDF...' : 'Download as PDF'}
        </Button>
      </Tooltip>

      {/* Dialog Content - Preview Area */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          p: 4,
          overflow: 'auto',
        }}
      >
        {/* Preview Container with A5 dimensions */}
        <Box
          ref={contentRef}
          sx={{
            width: '148mm', // A5 width
            height: '210mm', // A5 height - fixed to prevent overflow
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            '& *': {
              border: 0,
              boxSizing: 'border-box',
            },
            '& .receipt': {
              width: '100%',
              height: '100%',
              border: 0,
              margin: 0,
              padding: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </DialogContent>
    </Dialog>
  );
}
